import React, { useEffect, useLayoutEffect, useState } from "react";
import { format, startOfWeek, endOfWeek, addDays, isSameDay, startOfDay, endOfDay } from "date-fns";
import { DayButtonProps, DayPicker, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { ScheduleV2, ScheduleTimeoff, Unavailability, ScheduleV2Timeoff, ScheduleV2Holiday } from "../../../types";
import { GenericEventCard } from ".";
import { getMyScheduleDetails, getMySchedulesV2 } from "../../../redux/features/schedule/ScheduleApi";
import { DateTimeUtil } from "../../../utils";
import { ChevronLeft, ChevronRight, Loader2, Menu, Calendar, CalendarDays, CalendarRange } from "lucide-react";
import { useNavigate } from "react-router";
import { IncludeDeptType } from "../../../enums";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";

type CalendarView = 'daily' | 'weekly' | 'monthly';

const AppCalendar: React.FC = () => {
    const [selectedDay, setSelectedDay] = useState<Date | undefined>();
    const dispatch = useAppDispatch();
    const [schedules, setSchedules] = useState<ScheduleV2[]>([]);
    const [timeoffRequests, setTimeoffRequests] = useState<ScheduleV2Timeoff[]>([]);
    const [unavailabilityRequests, setUnavailabilityRequests] = useState<Unavailability[]>([]);
    const [holidays, setHolidays] = useState<ScheduleV2Holiday[]>([]);
    const { schedulesV2Map, timeoffRequestsV2Map, unavailabilityRequestsV2Map, holidaysV2Map, isLoadingSchedulesV2 } =
        useSelector((state: RootState) => state.schedule);
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [currentWeek, setCurrentWeek] = useState(() => new Date());
    const [currentDay, setCurrentDay] = useState(() => new Date());
    const [viewType, setViewType] = useState<CalendarView>(() => {
        const savedView = localStorage.getItem("calendar_view_type");
        return (savedView as CalendarView) || 'monthly';
    });
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [includeDept, setIncludeDept] = useState<IncludeDeptType>(() => {
        // Load dept view preference from localStorage
        const savedDeptView = localStorage.getItem("calendar_include_dept");
        return (savedDeptView as IncludeDeptType) || IncludeDeptType.MY_SCHEDULE;
    });

    // Custom setter that also saves to localStorage
    const setIncludeDeptWithPersistence = (value: IncludeDeptType) => {
        setIncludeDept(value);
        localStorage.setItem("calendar_include_dept", value);
    };

    // Custom setter for view type that also saves to localStorage
    const setViewTypeWithPersistence = (value: CalendarView) => {
        setViewType(value);
        localStorage.setItem("calendar_view_type", value);
    };

    const navigate = useNavigate();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOptionsOpen) {
                setIsOptionsOpen(false);
            }
        };

        if (isOptionsOpen) {
            document.addEventListener("click", handleClickOutside);
        }

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isOptionsOpen]);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Schedules"));
    }, [dispatch]);

    useEffect(() => {
        const today = new Date();
        const todayStr = format(today, "yyyy-MM-dd");
        const todaySchedules = schedulesV2Map[todayStr] || [];
        const todayTimeoffs = timeoffRequestsV2Map?.[todayStr] || [];
        const todayUnavailabilities = unavailabilityRequestsV2Map?.[todayStr] || [];
        const todayHolidays = holidaysV2Map?.[todayStr] || [];

        // Debug logging
        console.log("Calendar Debug - Today:", todayStr);
        console.log("Calendar Debug - Schedules:", todaySchedules);
        console.log("Calendar Debug - Timeoffs:", todayTimeoffs);
        console.log("Calendar Debug - Unavailabilities:", todayUnavailabilities);
        console.log("Calendar Debug - Holidays:", todayHolidays);
        console.log("Calendar Debug - All timeoffRequestsV2Map keys:", Object.keys(timeoffRequestsV2Map || {}));

        if (
            todaySchedules.length > 0 ||
            todayTimeoffs.length > 0 ||
            todayUnavailabilities.length > 0 ||
            todayHolidays.length > 0
        ) {
            setSelectedDay(today);
            setSchedules(todaySchedules);
            setTimeoffRequests(todayTimeoffs);
            setUnavailabilityRequests(todayUnavailabilities);
            setHolidays(todayHolidays);
        } else {
            setSelectedDay(undefined);
            setSchedules([]);
            setTimeoffRequests([]);
            setUnavailabilityRequests([]);
            setHolidays([]);
        }
    }, [schedulesV2Map, timeoffRequestsV2Map, unavailabilityRequestsV2Map, holidaysV2Map]);

    // Function to get date range based on view type
    const getDateRange = (viewType: CalendarView, currentDate: Date) => {
        let startDate: Date;
        let endDate: Date;

        switch (viewType) {
            case 'daily':
                startDate = startOfDay(currentDate);
                endDate = endOfDay(currentDate);
                break;
            case 'weekly':
                startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
                endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
                break;
            case 'monthly':
            default:
                const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
                startDate = new Date(startOfMonth);
                startDate.setDate(startOfMonth.getDate() - 7);
                endDate = new Date(endOfMonth);
                endDate.setDate(endOfMonth.getDate() + 7);
                break;
        }

        return { startDate, endDate };
    };

    // Function to fetch data based on current view
    const fetchDataForView = (viewType: CalendarView, currentDate: Date) => {
        const { startDate, endDate } = getDateRange(viewType, currentDate);

        dispatch(
            getMySchedulesV2({
                start_date: DateTimeUtil.formatDateToMySQL(startDate, true),
                end_date: DateTimeUtil.formatDateToMySQL(endDate, true),
                published_only: true,
                include_mydept: includeDept,
                include_timeoff_legacy: viewType === 'monthly',
                include_holiday: true,
                include_timeoff: true,
                include_unavailability: true,
            })
        );
    };

    useEffect(() => {
        fetchDataForView(viewType, 
            viewType === 'daily' ? currentDay : 
            viewType === 'weekly' ? currentWeek : 
            currentMonth
        );
    }, [dispatch, includeDept, viewType, currentDay, currentWeek, currentMonth]);

    const handleMonthChange = (newMonth: Date) => {
        setCurrentMonth(newMonth);
    };

    const handleWeekChange = (newWeek: Date) => {
        setCurrentWeek(newWeek);
    };

    const handleDayChange = (newDay: Date) => {
        setCurrentDay(newDay);
    };

    const handleScheduleClick = (schedule: ScheduleV2) => {
        dispatch(
            getMyScheduleDetails({
                schedule_id: schedule.schedules_id?.toString(),
            })
        );
        dispatch(setTopNavNameTitle("Schedule Details"));
        navigate("/m-schedule-details");
    };

    // Shared options menu component
    const OptionsMenu = () => {
        const handleSetUnavailability = () => {
            setIsOptionsOpen(false);
            navigate("/m-unavailability-form", {
                state: {
                    fromCalendar: true,
                },
            });
        };

        return (
            <div className="relative flex-shrink-0 ml-2">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOptionsOpen(!isOptionsOpen);
                    }}
                    className="text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors relative"
                >
                    <Menu size={20} />
                    {includeDept !== IncludeDeptType.MY_SCHEDULE && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                    )}
                </button>
                {isOptionsOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[220px]">
                        <div className="px-4 py-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 mb-2">VIEW TYPE</div>
                            <div className="space-y-2">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'daily'}
                                        onChange={() => setViewTypeWithPersistence('daily')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Daily</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'weekly'}
                                        onChange={() => setViewTypeWithPersistence('weekly')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Weekly</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="viewType"
                                        checked={viewType === 'monthly'}
                                        onChange={() => setViewTypeWithPersistence('monthly')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Monthly</span>
                                </label>
                            </div>
                        </div>
                        <div className="px-4 py-2 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 mb-2">VIEW SCHEDULES</div>
                            <div className="space-y-2">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.MY_SCHEDULE}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_SCHEDULE)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">My schedules only</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.MY_DEPARTMENT}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.MY_DEPARTMENT)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">My department</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="includeDept"
                                        checked={includeDept === IncludeDeptType.ALL}
                                        onChange={() => setIncludeDeptWithPersistence(IncludeDeptType.ALL)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">All schedules</span>
                                </label>
                            </div>
                        </div>
                        <button
                            onClick={handleSetUnavailability}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        >
                            Set Unavailability
                        </button>
                        <button
                            onClick={() => {
                                setIsOptionsOpen(false);
                                navigate("/open-shifts-master");
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors"
                        >
                            View open shifts
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Daily View Component
    const DailyView = () => {
        const goToPrevDay = () => {
            const prev = addDays(currentDay, -1);
            handleDayChange(prev);
        };

        const goToNextDay = () => {
            const next = addDays(currentDay, 1);
            handleDayChange(next);
        };

        const goToToday = () => {
            handleDayChange(new Date());
        };

        return (
            <div className="bg-white h-full flex flex-col">
                <div className="flex items-center w-full px-4 py-2 bg-white">
                    <button
                        onClick={goToPrevDay}
                        className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                    >
                        <ChevronLeft size={28} />
                    </button>
                    <div className="flex-1 text-center">
                        <h2 className="text-lg font-semibold">{format(currentDay, "EEEE, MMMM d, yyyy")}</h2>
                        {includeDept !== IncludeDeptType.MY_SCHEDULE && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                                {includeDept === IncludeDeptType.MY_DEPARTMENT 
                                    ? "Including department schedules" 
                                    : "Including all schedules"}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={goToNextDay}
                        className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                    >
                        <ChevronRight size={28} />
                    </button>
                    <button
                        onClick={goToToday}
                        className="ml-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Today
                    </button>
                    <OptionsMenu />
                </div>
                {/* Spacer to fill remaining height */}
                {/* <div className="flex-1 min-h-96"></div>  */}
            </div>
        );
    };

    // Weekly View Component
    const WeeklyView = () => {
        const goToPrevWeek = () => {
            const prev = addDays(currentWeek, -7);
            handleWeekChange(prev);
        };

        const goToNextWeek = () => {
            const next = addDays(currentWeek, 7);
            handleWeekChange(next);
        };

        const goToThisWeek = () => {
            handleWeekChange(new Date());
        };

        const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

        return (
            <div className="bg-white h-full flex flex-col">
                <div className="flex items-center w-full px-4 py-2 bg-white">
                    <button
                        onClick={goToPrevWeek}
                        className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                    >
                        <ChevronLeft size={28} />
                    </button>
                    <div className="flex-1 text-center">
                        <h2 className="text-lg font-semibold">
                            {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
                        </h2>
                        {includeDept !== IncludeDeptType.MY_SCHEDULE && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                                {includeDept === IncludeDeptType.MY_DEPARTMENT 
                                    ? "Including department schedules" 
                                    : "Including all schedules"}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={goToNextWeek}
                        className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                    >
                        <ChevronRight size={28} />
                    </button>
                    <button
                        onClick={goToThisWeek}
                        className="ml-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        This Week
                    </button>
                    <OptionsMenu />
                </div>
                <div className="grid grid-cols-7 border-b border-gray-200">
                    {Array.from({ length: 7 }, (_, i) => {
                        const day = addDays(weekStart, i);
                        const dateStr = format(day, "yyyy-MM-dd");
                        const hasSchedule = schedulesV2Map[dateStr]?.length > 0;
                        const hasTimeoff = (timeoffRequestsV2Map && timeoffRequestsV2Map[dateStr]?.length > 0) || false;
                        const hasUnavailability = (unavailabilityRequestsV2Map && unavailabilityRequestsV2Map[dateStr]?.length > 0) || false;
                        const hasHoliday = (holidaysV2Map && holidaysV2Map[dateStr]?.length > 0) || false;
                        const isToday = isSameDay(day, new Date());
                        const isSelected = selectedDay && isSameDay(day, selectedDay);

                        return (
                            <div
                                key={i}
                                className={`p-2 text-center border-r border-gray-200 last:border-r-0 cursor-pointer hover:bg-gray-50 ${
                                    isSelected ? "bg-blue-100" : ""
                                }`}
                                onClick={() => {
                                    const schedules = schedulesV2Map[dateStr] || [];
                                    const timeoffs = (timeoffRequestsV2Map && timeoffRequestsV2Map[dateStr]) || [];
                                    const unavailabilities = (unavailabilityRequestsV2Map && unavailabilityRequestsV2Map[dateStr]) || [];
                                    const holidays = (holidaysV2Map && holidaysV2Map[dateStr]) || [];

                                    setSchedules(schedules);
                                    setTimeoffRequests(timeoffs);
                                    setUnavailabilityRequests(unavailabilities);
                                    setHolidays(holidays);
                                    setSelectedDay(day);
                                }}
                            >
                                <div className={`text-xs text-gray-500 mb-1 ${isToday ? "font-semibold text-blue-600" : ""}`}>
                                    {format(day, "EEE")}
                                </div>
                                <div className={`text-sm font-medium ${isToday ? "text-blue-600" : ""}`}>
                                    {day.getDate()}
                                </div>
                                <div className="flex justify-center gap-1 mt-1">
                                    {hasSchedule && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Schedule" />}
                                    {hasTimeoff && <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Time off" />}
                                    {hasUnavailability && <span className="w-1.5 h-1.5 rounded-full bg-gray-500" title="Unavailable" />}
                                    {hasHoliday && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Holiday" />}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Spacer to fill remaining height */}
                {/* <div className="flex-1 min-h-96"></div> */}
            </div>
        );
    };

    const CalendarHeader = ({ month, onMonthChange }: { month: Date; onMonthChange: (date: Date) => void }) => {
        const buttonSize = 28;
        const goToPrevMonth = () => {
            const prev = new Date(month.getFullYear(), month.getMonth() - 1, 1);
            onMonthChange(prev);
        };

        const goToNextMonth = () => {
            const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
            onMonthChange(next);
        };

        return (
            <div className="flex items-center w-full px-4 py-2 bg-white">
                <button
                    onClick={goToPrevMonth}
                    className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                >
                    <ChevronLeft size={buttonSize} />
                </button>
                <div className="flex-1 text-center">
                    <h2 className="text-lg font-semibold">{format(month, "MMMM yyyy")}</h2>
                    {includeDept !== IncludeDeptType.MY_SCHEDULE && (
                        <div className="text-xs text-blue-600 font-medium mt-1">
                            {includeDept === IncludeDeptType.MY_DEPARTMENT 
                                ? "Including department schedules" 
                                : "Including all schedules"}
                        </div>
                    )}
                </div>
                <button
                    onClick={goToNextMonth}
                    className="text-blue-500 p-2 rounded-full hover:bg-blue-100 flex-shrink-0"
                >
                    <ChevronRight size={buttonSize} />
                </button>
                <OptionsMenu />
            </div>
        );
    };

    const CustomDayButton = ({ day, modifiers, ...props }: DayButtonProps) => {
        const { date } = day;
        const dateStr = format(date, "yyyy-MM-dd");
        const hasSchedule = schedulesV2Map[dateStr]?.length > 0;
        const hasTimeoff = (timeoffRequestsV2Map && timeoffRequestsV2Map[dateStr]?.length > 0) || false;
        const hasUnavailability =
            (unavailabilityRequestsV2Map && unavailabilityRequestsV2Map[dateStr]?.length > 0) || false;
        const hasHoliday = (holidaysV2Map && holidaysV2Map[dateStr]?.length > 0) || false;

        const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
        const isSelected = selectedDay?.toDateString() === date.toDateString();

        const handleClick = () => {
            const schedules = schedulesV2Map[dateStr] || [];
            const timeoffs = (timeoffRequestsV2Map && timeoffRequestsV2Map[dateStr]) || [];
            const unavailabilities = (unavailabilityRequestsV2Map && unavailabilityRequestsV2Map[dateStr]) || [];
            const holidays = (holidaysV2Map && holidaysV2Map[dateStr]) || [];

            setSchedules(schedules);
            setTimeoffRequests(timeoffs);
            setUnavailabilityRequests(unavailabilities);
            setHolidays(holidays);
            setSelectedDay(date);
        };

        return (
            <button
                {...props}
                type="button"
                onClick={handleClick}
                className="flex flex-col items-center justify-center w-full h-full p-1"
            >
                <div
                    className={`
                        w-8 h-8 flex items-center justify-center rounded-full text-sm transition-all
                        ${isSelected ? "ring-2 ring-blue-500" : ""}
                        ${isToday ? "border border-blue-500 text-blue-500 font-semibold" : ""}
                    `}
                >
                    {date.getDate()}
                </div>
                <div className="flex gap-1 mt-1">
                    {hasSchedule && <span className="w-2 h-2 rounded-full bg-blue-500" title="Schedule" />}
                    {hasTimeoff && <span className="w-2 h-2 rounded-full bg-green-500" title="Time off" />}
                    {hasUnavailability && (
                        <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: "#6c757d" }}
                            title="Unavailable"
                        />
                    )}
                    {hasHoliday && (
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#DC2626" }} title="Holiday" />
                    )}
                </div>
            </button>
        );
    };

    // Function to get events for a specific date
    const getEventsForDate = (date: Date) => {
        const dateStr = format(date, "yyyy-MM-dd");
        const schedules = schedulesV2Map[dateStr] || [];
        const timeoffs = (timeoffRequestsV2Map && timeoffRequestsV2Map[dateStr]) || [];
        const unavailabilities = (unavailabilityRequestsV2Map && unavailabilityRequestsV2Map[dateStr]) || [];
        const holidays = (holidaysV2Map && holidaysV2Map[dateStr]) || [];

        return { schedules, timeoffs, unavailabilities, holidays };
    };

    // Function to render events for a specific date
    const renderEventsForDate = (date: Date) => {
        const { schedules, timeoffs, unavailabilities, holidays } = getEventsForDate(date);
        const isSelected = selectedDay && isSameDay(date, selectedDay);

        if (!isSelected) return null;

        return (
            <div className="space-y-2">
                {/* Render Schedules */}
                {schedules.map((schedule: ScheduleV2, index) => (
                    <GenericEventCard
                        key={`schedule-${index}`}
                        data={schedule}
                        dataType="schedule"
                        includeTeam={includeDept !== IncludeDeptType.MY_SCHEDULE}
                        onClick={(data) => {
                            if (data && "schedules_id" in data) {
                                handleScheduleClick(data as ScheduleV2);
                            }
                        }}
                    />
                ))}

                {/* Render Timeoff Requests */}
                {timeoffs.map((timeoff: ScheduleV2Timeoff, index) => (
                    <GenericEventCard
                        key={`timeoff-${index}`}
                        data={timeoff}
                        dataType="scheduleTimeoff"
                        includeTeam={includeDept !== IncludeDeptType.MY_SCHEDULE}
                        className="pending-stripe"
                        onClick={(data) => {
                            console.log("Timeoff clicked:", data);
                        }}
                    />
                ))}

                {/* Render Unavailability Requests */}
                {unavailabilities.map((unavailability: Unavailability, index) => (
                    <GenericEventCard
                        key={`unavailability-${index}`}
                        data={unavailability}
                        dataType="unavailability"
                        includeTeam={includeDept !== IncludeDeptType.MY_SCHEDULE}
                        className="pending-stripe"
                    />
                ))}

                {/* Render Holidays */}
                {holidays.map((holiday: ScheduleV2Holiday, index) => (
                    <GenericEventCard
                        key={`holiday-${index}`}
                        data={holiday}
                        dataType="holiday"
                        includeTeam={includeDept !== IncludeDeptType.MY_SCHEDULE}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col lg:flex-row lg:gap-6 lg:px-6">
            {/* Calendar Section */}
            <div className="bg-white lg:w-1/2 lg:flex-shrink-0 h-full">
                {viewType === 'daily' && <DailyView />}
                {viewType === 'weekly' && <WeeklyView />}
                {viewType === 'monthly' && (
                    <div className="bg-white h-full flex flex-col">
                        <CalendarHeader month={currentMonth} onMonthChange={handleMonthChange} />
                        <DayPicker
                            mode="single"
                            selected={selectedDay}
                            className="text-gray-800"
                            weekStartsOn={1}
                            month={currentMonth}
                            components={{
                                DayButton: CustomDayButton,
                                MonthCaption: (props) => {
                                    return <></>;
                                },
                            }}
                            captionLayout="label"
                            hideNavigation={true}
                            onMonthChange={handleMonthChange}
                            classNames={{
                                chevron: `${getDefaultClassNames().chevron} w-full h-full text-gray-600`,
                            }}
                            styles={{
                                head: { fontWeight: "600", fontSize: "0.8rem", color: "#6B7280" },
                                week: { borderBottom: "1px solid #E5E7EB" },
                                weekdays: {
                                    fontWeight: "600",
                                    fontSize: "0.8rem",
                                    color: "#6B7280",
                                    borderBottom: "1px solid #E5E7EB",
                                },
                                head_cell: {
                                    width: "auto",
                                    marginTop: "2rem",
                                    padding: "2.5rem 0",
                                    minWidth: "0",
                                    whiteSpace: "nowrap",
                                },
                                table: {
                                    width: "100%",
                                    maxWidth: "100%",
                                },
                                day: {
                                    padding: "1rem 0",
                                    fontSize: "1rem",
                                    textAlign: "center",
                                    width: "calc(100vw / 7)",
                                    borderRadius: "0.5rem",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    transition: "background-color 0.2s, color 0.2s",
                                },
                                selected: {
                                    backgroundColor: "rgb(34, 197, 94)",
                                    color: "white",
                                },
                                today: {
                                    fontWeight: "bold",
                                    color: "white",
                                    backgroundColor: "#3B82F6",
                                    borderRadius: "9999px",
                                    padding: "0.4rem",
                                    border: "2px solid #2563EB",
                                },
                                outside: { color: "#D1D5DB" },
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Events Section */}
            <div className="flex-grow-6 bg-white w-full lg:w-1/2 lg:flex-shrink-0 items-start rounded-t-lg px-2 pt-2 overflow-y-auto">
                {isLoadingSchedulesV2 ? (
                    <div className="flex justify-center items-start py-4">
                        <Loader2 className="animate-spin text-blue-500 w-6 h-6" />
                    </div>
                ) : viewType === 'daily' ? (
                    // Daily view - show events for the current day
                    renderEventsForDate(currentDay) || (
                        <p className="text-center text-gray-500 mt-4 font-bold">No events for {format(currentDay, "EEEE, MMMM d, yyyy")}</p>
                    )
                ) : viewType === 'weekly' ? (
                    // Weekly view - show events for the selected day
                    selectedDay ? (
                        renderEventsForDate(selectedDay) || (
                            <p className="text-center text-gray-500 mt-4 font-bold">No events for {format(selectedDay, "EEEE, MMMM d, yyyy")}</p>
                        )
                    ) : (
                        <p className="text-center text-gray-500 mt-4 font-bold">Select a day to view events</p>
                    )
                ) : (
                    // Monthly view - show events for the selected day
                    selectedDay ? (
                        renderEventsForDate(selectedDay) || (
                            <p className="text-center text-gray-500 mt-4 font-bold">No events for {format(selectedDay, "EEEE, MMMM d, yyyy")}</p>
                        )
                    ) : (
                        <p className="text-center text-gray-500 mt-4 font-bold">Select a day to view events</p>
                    )
                )}
            </div>
        </div>
    );
};

export default AppCalendar;
